import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { useDataFormatter } from "./utils/dataFormatter";
import { useEffect, useReducer, Reducer, useCallback } from "react";
import { getMeetingCounters, getMeetingSummaries } from "@services/meetings";
import { getDeviceCounters } from "@services/devices";
import { getTicketsCounters } from "@services/tickets";
import { usePolling } from "@common/hooks";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";

import {
  canViewDevice,
  canViewMeeting,
  canViewRoom,
  canViewTicket,
  hasLicense,
} from "@common/utils/user";
import { toEndOfDay } from "@common/utils/localTime.utils";

export interface RealTimeDataState {
  deviceCount?: {
    realTimeCard: RealTimeCardCommonProps;
    data: RealTimeDevicesDataProps[];
  };
  ticketCount?: RealTimeTicketProps;
  meetingCount?: {
    realTimeCard: RealTimeCardCommonProps;
    data: RealTimeMeetingDataWithLabelProps[];
  };
  dateCount: RealTimeDateProps;
  notificationCount?: number;
}
export const useRealTimeData = () => {
  const format = useDataFormatter();
  const [user] = useAuthenticatedUser();
  const accountCanViewMeetings = hasLicense(user?.account, "premier");
  const canViewMeetings = accountCanViewMeetings && canViewMeeting(user);
  const canViewTickets = canViewTicket(user);
  const canViewRoomsAndDevices = canViewRoom(user) && canViewDevice(user);
  const [state, dispatch] = useReducer(
    (state: RealTimeDataState, changes: Partial<RealTimeDataState>) => ({
      ...state,
      ...changes,
    }),
    {
      dateCount: format.date(),
    }
  );
  const [data, selectAll] = useUserSelectedAccounts();
  const accounts = selectAll ? undefined : data;
  const fetchDevices = async (accounts?: typeof data) => {
    try {
      dispatch({
        deviceCount: format.device(await getDeviceCounters(accounts)),
      });
    } catch (e) {}
  };

  const fetchMeetings = async (accounts?: typeof data) => {
    try {
      const startDate = new Date();
      let endDate = toEndOfDay(startDate);
      dispatch({
        meetingCount: format.meeting(
          await getMeetingCounters(startDate, endDate, accounts)
        ),
      });
    } catch (e) {}
  };

  const fetchTickets = async (accounts?: typeof data) => {
    try {
      dispatch({
        ticketCount: format.ticket(
          await getTicketsCounters({ accountId: accounts, status: ["Open"] })
        ),
      });
    } catch (e) {}
  };

  const refetch = useCallback(() => {
    if (selectAll === null) return;
    if (canViewRoomsAndDevices) fetchDevices(accounts);
    if (canViewMeetings) fetchMeetings(accounts);
    if (canViewTickets) fetchTickets(accounts);
  }, [
    selectAll,
    accounts,
    canViewRoomsAndDevices,
    canViewMeetings,
    canViewTickets,
  ]);

  const poll = usePolling(refetch);

  useEffect(() => {
    if (selectAll !== null) {
      poll.start();
    }
    return () => poll.stop();
  }, [
    selectAll,
    accounts,
    canViewRoomsAndDevices,
    canViewMeetings,
    canViewTickets,
  ]);

  useEffect(() => {
    const minuteInMs = 60e3;
    let interval: NodeJS.Timeout;
    const tempDate = format.date();
    if (
      state.dateCount.data.time !== tempDate.data.time ||
      state.dateCount.data.date !== tempDate.data.date
    )
      dispatch({ dateCount: tempDate });
    let timeout = setTimeout(() => {
      dispatch({ dateCount: format.date() });
      interval = setInterval(() => {
        dispatch({ dateCount: format.date() });
      }, minuteInMs);
    }, minuteInMs - (new Date().getTime() % minuteInMs));

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [user?.details.timeZone]);

  return state;
};
